import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/shared/auth';
import type { Maybe } from '@/shared/libs';
import { LoaderOverlay, PageHeader } from '@/shared/ui';
import { canAccessRole, resolveUserRole, UserRoleEnum } from '@entities/User';
import type { Offer } from '@psp-aggregator/sdk';
import { AppShell, useNavBadges } from '@widgets/AppShell';
import { DealsPipeline } from '@widgets/DealsPipeline';
import { DealTicket } from '@widgets/DealTicket';
import { OffersList } from '@widgets/OffersList';
import { PlatformOverview } from '@widgets/PlatformOverview';
import { ProposalBuilder } from '@widgets/ProposalBuilder';
import { ProvidersList } from '@widgets/ProvidersList';
import { UsersManagement } from '@widgets/UsersManagement';

import { ADMIN_TABS, AdminTab, isAdminTab } from '../../model/admin-tabs';
import { ADMIN_PROVIDER_STATUS_OPTIONS } from '../../model/provider-status';

export interface AdminDashboardViewProps {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
}

const ADMIN_HEADING_KEY: Record<
  AdminTab,
  | 'pages.admin.headings.overview'
  | 'pages.admin.headings.users'
  | 'pages.admin.headings.providers'
  | 'pages.admin.headings.offers'
  | 'pages.admin.headings.pipeline'
> = {
  [AdminTab.Offers]: 'pages.admin.headings.offers',
  [AdminTab.Overview]: 'pages.admin.headings.overview',
  [AdminTab.Pipeline]: 'pages.admin.headings.pipeline',
  [AdminTab.Providers]: 'pages.admin.headings.providers',
  [AdminTab.Users]: 'pages.admin.headings.users',
};

const TABS_WITH_OWN_HEADER = new Set<AdminTab>([
  AdminTab.Offers,
  AdminTab.Providers,
  AdminTab.Users,
]);

export const AdminDashboardView: FC<AdminDashboardViewProps> = ({
  activeTab,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedDealId, setSelectedDealId] =
    useState<Maybe<string>>(undefined);
  const [proposalOffers, setProposalOffers] = useState<Offer[]>([]);
  const [proposalOpen, setProposalOpen] = useState(false);
  const role = user ? resolveUserRole(user.role) : undefined;
  const badges = useNavBadges(role);
  const dealsBadge = badges.deals > 0 ? badges.deals : undefined;

  const isSuperAdmin = user
    ? canAccessRole(user.role, [UserRoleEnum.Admin])
    : false;

  const tabs = useMemo(
    () =>
      ADMIN_TABS.filter((tab) => !tab.adminOnly || isSuperAdmin).map((tab) => ({
        badge: tab.key === AdminTab.Pipeline ? dealsBadge : undefined,
        badgeTone: 'rose' as const,
        icon: tab.icon,
        key: tab.key,
        label: t(tab.labelKey),
      })),
    [dealsBadge, isSuperAdmin, t],
  );

  useEffect(() => {
    if (activeTab === AdminTab.Users && !isSuperAdmin) {
      onTabChange(AdminTab.Overview);
    }
  }, [activeTab, isSuperAdmin, onTabChange]);

  if (!user) {
    return <LoaderOverlay show />;
  }

  const resolvedTab =
    activeTab === AdminTab.Users && !isSuperAdmin
      ? AdminTab.Overview
      : activeTab;

  const headingKey =
    ADMIN_HEADING_KEY[resolvedTab] ?? ADMIN_HEADING_KEY[AdminTab.Overview];
  const showPageHeader = !TABS_WITH_OWN_HEADER.has(resolvedTab);

  const goToPipeline = (): void => {
    onTabChange(AdminTab.Pipeline);
  };

  const handleTabChange = (tab: string): void => {
    if (isAdminTab(tab)) {
      onTabChange(tab);
    }
  };

  return (
    <AppShell activeTab={resolvedTab} tabs={tabs} onTabChange={handleTabChange}>
      <div>
        {showPageHeader && <PageHeader title={t(headingKey)} />}

        <div className={showPageHeader ? 'mt-6' : undefined}>
          {resolvedTab === AdminTab.Users && isSuperAdmin && (
            <UsersManagement
              title={t(ADMIN_HEADING_KEY[AdminTab.Users])}
              onGoToDeals={goToPipeline}
            />
          )}

          {resolvedTab === AdminTab.Providers && (
            <ProvidersList
              statusOptions={ADMIN_PROVIDER_STATUS_OPTIONS}
              title={t(ADMIN_HEADING_KEY[AdminTab.Providers])}
              onGoToDeals={goToPipeline}
            />
          )}

          {resolvedTab === AdminTab.Offers && (
            <OffersList
              selectedOffers={proposalOffers}
              title={t(ADMIN_HEADING_KEY[AdminTab.Offers])}
              onGoToDeals={goToPipeline}
              onSelectedOffersChange={setProposalOffers}
              onSendProposal={() => setProposalOpen(true)}
            />
          )}

          {resolvedTab === AdminTab.Pipeline && (
            <DealsPipeline onOpenDeal={setSelectedDealId} />
          )}

          {resolvedTab === AdminTab.Overview && <PlatformOverview />}
        </div>
      </div>
      {proposalOpen && (
        <ProposalBuilder
          offers={proposalOffers}
          source="admin_outbound"
          onClose={() => setProposalOpen(false)}
          onCreated={() => setProposalOffers([])}
        />
      )}
      {!!selectedDealId && (
        <DealTicket
          dealId={selectedDealId}
          onClose={() => setSelectedDealId(undefined)}
        />
      )}
    </AppShell>
  );
};

AdminDashboardView.displayName = 'AdminDashboardView';
